/** Yield one frame so the UI can paint its reaction (spinner, disabled
 * button) BEFORE heavy work starts. Work that begins inside the click
 * handler lands inside the INP window and, worse, leaves the control looking
 * dead — prod data showed 77% of repeat presses arriving within 5 s of the
 * first.
 *
 * The wait is bounded, because a frame is not guaranteed to arrive: a hidden
 * tab never paints, so `requestAnimationFrame` never fires there. Pressing
 * Start and switching apps used to leave the upload un-started for as long as
 * the person stayed away — measured at 234 s with no request ever reaching
 * the backend — which is the ordinary way a phone is used, not an edge case.
 */

/** How long to wait for a frame that may never come. Long enough that a
 * visible tab always paints first (a frame is ~16 ms, a slow one ~50), short
 * enough that a backgrounded tab loses nothing worth measuring. */
const NO_FRAME_MS = 200

export function afterPaint(): Promise<void> {
  return new Promise((resolve) => {
    let settled = false
    const done = () => {
      if (settled) return
      settled = true
      resolve()
    }

    // Nothing is going to paint — don't pretend to wait for it
    if (typeof document !== 'undefined' && document.hidden) {
      setTimeout(done, 0)
      return
    }

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => setTimeout(done, 0))
    }
    // The tab can also go hidden between this line and the next frame, so the
    // timer is armed either way and whichever comes first wins
    setTimeout(done, NO_FRAME_MS)
  })
}
